import { AllDataFromSalesForce } from '@/shared/types/allOldData'
import { getSalesForcePortFolioData } from './utils'

export interface RestructuredOldStoriesDataItem {
  stories: RestructuredStory[]
  design: RestructureDesign
}

/**
 * Каждая история внутри массива `stories`.
 * (Набор полей взят из оригинальной функции restructureOldStoriesData)
 */
export interface RestructuredStory {
  id: string
  status: 'PUBLISHED'
  createdAt: string
  isPublishedWeb: boolean
  isPublishedBrightr: boolean
  isFeatured: boolean
  isSalesforce: boolean
  companyName: string
  layout: boolean
  salesforce: unknown // если getSalesForcePortFolioData(...) возвращает свой объект, можно описать точнее
  project: RestructuredStoryProject
  client: RestructuredStoryClient
  technologies: RestructuredStoryTechnology[]
  teamMembers: RestructuredStoryMember[]
  keyMoments: RestructuredStoryKeyMoments
  feedback: RestructuredStoryFeedback | null
  seo: RestructuredStorySeo
  meeting: RestructuredStoryMeeting
}

/**
 * Объект `design` для каждого «Result».
 */
export interface RestructureDesign {
  primaryFontType: string
  secondaryFontType: string
  generalBackgroundColor: string
  primaryCoverFontColor: string
  primaryFontColor: string
  secondaryFontColor: string
  accentBackgroundColor: string
  accentPrimaryFontColor: string
  accentSecondaryFontColor: string
  coverBackgroundColor?: string
  websiteUrl: string
  contactUsLinkUrl: string
  logoImageUrl: string
  storyPageBannerImageUrl: string
}

// ---- Вспомогательные вложенные структуры ----

export interface RestructuredStoryProject {
  portfolioItemName: string
  industry: string
  projectBudget: string
  projectLength: string
  description: string
  deepDescription: string
  teamSize: number
  storyImageUrl: string
}

export interface RestructuredStoryClient {
  clientName: string
  aboutClient: string
  whyNow: string
  clientCulture: string
  clientLogoImageUrl: string
}

export interface RestructuredStoryTechnology {
  name: string
  logoImageUrl: string
}

export interface RestructuredStoryMember {
  role: string
  firstName: string
  lastName: string
  email: string
  linkedInProfileUrl: string | null
  teamMemberImageUrl: string
}

export interface RestructuredStoryKeyMoments {
  momentOfTrust: string
  introductionMoment: string
  dealClarityMoment: string
  roiMoment: string
  ribbonCuttingMoment: string
  eurekaMoment: string
  redMoment: string

  momentOfTrustImageUrl: string
  introductionMomentImageUrl: string
  dealClarityMomentImageUrl: string
  roiMomentImageUrl: string
  ribbonCuttingMomentImageUrl: string
  eurekaMomentImageUrl: string
  redMomentImageUrl: string
}

export interface RestructuredStoryFeedback {
  firstName: string
  lastName: string
  title: string
  testimonial: string
}

export interface RestructuredStorySeo {
  metaName: string
  metaDescription: string
}

export interface RestructuredStoryMeeting {
  meetingContact: RestructuredStoryMember
  modalDescription: string
  bannerDescription: string
  calendarLink: string
}

/**
 * Собственно, функция, которая принимает AllData (старый формат)
 * и возвращает массив «приведенных» объектов.
 */
export function restructureOldStoriesData(
  data: AllDataFromSalesForce | null | undefined,
): RestructuredOldStoriesDataItem[] {
  if (!data?.results) {
    return []
  }

  // 1) Пробегаемся по каждому "Result" внутри AllData
  const items: RestructuredOldStoriesDataItem[] = data.results.map(companyResult => {
    // Если в объекте нет stories, возвращаем "пустой" вариант
    if (!companyResult?.stories || companyResult.stories.length === 0) {
      return {
        stories: [],
        design: {
          primaryFontType: '',
          secondaryFontType: '',
          generalBackgroundColor: '#FFFFFF',
          primaryCoverFontColor: '#FFFFFF',
          primaryFontColor: '#000000',
          secondaryFontColor: '#000000',
          accentBackgroundColor: '#000000',
          accentPrimaryFontColor: '#000000',
          accentSecondaryFontColor: '#000000',
          websiteUrl: '',
          contactUsLinkUrl: '',
          logoImageUrl: '',
          storyPageBannerImageUrl: '',
        },
      }
    }

    // 2) Формируем массив stories
    const salesforceData = getSalesForcePortFolioData(companyResult)
    const restructuredStories: RestructuredStory[] = companyResult.stories.map(story => {
      const salesforceStory = salesforceData.stories.filter(item => item.id === story?.storyId)[0]
      const feedback =
        story?.testimonialsData?.[0]?.author &&
        story?.testimonialsData?.[0]?.industry &&
        story?.testimonialsData?.[0]?.description
          ? {
              firstName: story.testimonialsData[0].author,
              lastName: '',
              title: story.testimonialsData[0].industry,
              testimonial: story.testimonialsData[0].description,
            }
          : null

      return {
        id: story.storyId,
        status: 'PUBLISHED',
        createdAt: story.LastModifiedDate,
        isPublishedWeb: false,
        isPublishedBrightr: true,
        isFeatured: false,
        isSalesforce: true,
        companyName: companyResult.title,
        layout: story.topBannerData.layout === 'Original',
        salesforce: salesforceStory, // можно указать конкретный тип вместо unknown
        project: {
          portfolioItemName: story.projectSectionData.title,
          industry: story.filterValues.Industry?.[0] || '',
          projectBudget: story.projectBoundariesData.rightStatus || '',
          projectLength: story.filterValues.Timeline?.[0] || '',
          description: story.projectSectionData.paragraph2,
          deepDescription: story.projectSectionData.deepdescription,
          teamSize: story.projectSectionData.teamSize ?? 0,
          storyImageUrl: story.projectSectionData.imgSrc,
        },
        client: {
          clientName: story.topBannerData.table.find(item => item.keyName === 'CLIENT NAME')?.valueName || '',
          aboutClient: story.valueSectionsData?.[2]?.description || '',
          whyNow: story.valueSectionsData?.[1]?.description || '',
          clientCulture: story.valueSectionsData?.[0]?.description || '',
          clientLogoImageUrl: story.topBannerData.logoSrc,
        },
        technologies: story.filterValues.Technologies.map((tech, index) => ({
          name: tech,
          logoImageUrl: story.technologiesIconSrc[index] || '',
        })),
        teamMembers: story.teamProfilesData.map(member => ({
          role: member.role,
          firstName: member.name,
          lastName: '', // нет в исходных данных
          email: 'teammember2_company_b@mail.com', // «заглушка»
          linkedInProfileUrl: member.serviceLink || null,
          teamMemberImageUrl: member.dpSrc || '',
        })),
        keyMoments: {
          momentOfTrust: story.keyMoments.momentOfTrust,
          momentOfTrustImageUrl: story.keyMoments.momentOfTrustImage,
          introductionMoment: story.keyMoments.introductionMoment,
          introductionMomentImageUrl: story.keyMoments.introductionMomentImage,
          dealClarityMoment: story.keyMoments.dealClarityMoment,
          dealClarityMomentImageUrl: story.keyMoments.dealClarityMomentImage,
          roiMoment: story.keyMoments.roiMoment,
          roiMomentImageUrl: story.keyMoments.roiMomentImage,
          ribbonCuttingMoment: story.keyMoments.ribbonCuttingMoment,
          ribbonCuttingMomentImageUrl: story.keyMoments.ribbonCuttingMomentImage,
          eurekaMoment: story.keyMoments.eurekaMoment,
          eurekaMomentImageUrl: story.keyMoments.eurekaMomentImage,
          redMoment: story.keyMoments.redMoment,
          redMomentImageUrl: story.keyMoments.redMomentImage,
        },
        feedback,
        seo: {
          metaName: story.metadataname || '',
          metaDescription: story.metadatadescription || '',
        },
        meeting: {
          meetingContact: {
            role: story.bookMeetingData.title,
            firstName: story.bookMeetingData.contactName,
            lastName: '',
            email: 'teammember4_company_b@mail.com',
            linkedInProfileUrl: null,
            teamMemberImageUrl: story.bookMeetingData.headshotUrl,
          },
          modalDescription: story.bookMeetingData.modalDescription,
          bannerDescription: story.bookMeetingData.bannerDescription,
          calendarLink: story.bookMeetingData.calendlyLink,
        },
      }
    })

    // 3) Собираем design-данные для данного «Result»
    //    Берём из первой истории d.stories[0]
    const firstStory = companyResult.stories[0]
    const design: RestructureDesign = {
      primaryFontType: firstStory.primaryfont,
      secondaryFontType: firstStory.secondaryfont,
      generalBackgroundColor: firstStory.bgcolor || '#FFFFFF',
      primaryCoverFontColor: firstStory.secondaryheaderfontcolor || '#FFFFFF',
      primaryFontColor: firstStory.primaryfontcolor || '#000000',
      secondaryFontColor: firstStory.secondaryfontcolor || '#000000',
      accentBackgroundColor: firstStory.primarycolor || '#000000',
      accentPrimaryFontColor: firstStory.primaryfontcolor || '#000000',
      accentSecondaryFontColor: firstStory.secondaryfontcolor || '#000000',
      websiteUrl: firstStory.website || '',
      contactUsLinkUrl: companyResult.contact_us || '',
      logoImageUrl: companyResult.logo || '',
      storyPageBannerImageUrl: firstStory.storypagebanner || '',
    }

    return {
      stories: restructuredStories,
      design,
    }
  })

  return items
}

export const stripHtml = (html: string) => html.replace(/<\/?[^>]+(>|$)/g, '').trim()